import { motion, AnimatePresence } from "framer-motion";
import { FaGithub, FaExternalLinkAlt, FaTimes, FaStar, FaCode, FaLayerGroup } from "react-icons/fa";
import { useState, useEffect } from "react";

const Information = ({ project, onClose }) => {
  // Standardizing colors for reliability
  const colorVariants = {
    pink: {
      from: "from-pink-500",
      to: "to-rose-500",
      text: "text-pink-400",
      bg: "bg-pink-500",
      border: "border-pink-500/30",
      shadow: "shadow-pink-500/20",
    },
    cyan: {
      from: "from-cyan-500",
      to: "to-blue-500",
      text: "text-cyan-400",
      bg: "bg-cyan-500",
      border: "border-cyan-500/30",
      shadow: "shadow-cyan-500/20",
    },
    purple: {
      from: "from-violet-500",
      to: "to-purple-500",
      text: "text-violet-400",
      bg: "bg-violet-500",
      border: "border-violet-500/30",
      shadow: "shadow-violet-500/20",
    }
  };

  const [selectedImage, setSelectedImage] = useState(0);
  const theme = colorVariants[project.accentColor] || colorVariants.cyan;

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  return (
    <AnimatePresence>
      {/* 
         1. FIXED BACKDROP 
         Changed bg-black/80 to bg-slate-900/40 to make it less "black" 
         and let the background site show through via blur.
      */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4 md:p-6"
      >
        {/* 2. MAIN MODAL CONTAINER */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-6xl max-h-[90vh] flex flex-col md:flex-row bg-slate-900 rounded-3xl overflow-y-auto md:overflow-hidden border border-white/10 shadow-2xl"
          style={{ touchAction: 'pan-y', overscrollBehavior: 'contain' }}
        >

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 hover:bg-white/20 text-white/70 hover:text-white transition-all backdrop-blur-md border border-white/10"
          >
            <FaTimes size={20} />
          </button>

          {/* === LEFT COLUMN: VISUALS === */}
          <div className="w-full md:w-[45%] bg-slate-950/50 flex flex-col border-b md:border-b-0 md:border-r border-white/5 relative">

            {/* Subtle background gradient for left side */}
            <div className={`absolute top-0 left-0 w-full h-full bg-gradient-to-br ${theme.from} to-transparent opacity-[0.05] pointer-events-none`} />

            <div className="p-6 md:p-8 flex flex-col md:h-full md:overflow-y-auto custom-scrollbar">

              {/* Main Image Stage */}
              <div className="flex-1 flex items-center justify-center min-h-[300px] mb-6">
                <div className="relative w-full">

                  {/* GLOW EFFECT (Photos) */}
                  <div className={`absolute -inset-4 rounded-full blur-2xl opacity-20 ${theme.bg}`}></div>

                  <motion.div
                    layoutId="viewport"
                    className="relative rounded-lg overflow-hidden border border-white/10 bg-slate-800 shadow-2xl"
                  >
                    {/* Browser Bar */}
                    <div className="h-6 bg-slate-900 border-b border-white/5 flex items-center px-3 gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-red-500/50" />
                      <div className="w-2 h-2 rounded-full bg-yellow-500/50" />
                      <div className="w-2 h-2 rounded-full bg-green-500/50" />
                    </div>
                    <img
                      src={project.images[selectedImage]}
                      alt="Project Screenshot"
                      className="w-full h-auto object-cover aspect-video"
                    />
                  </motion.div>
                </div>
              </div>

              {/* Thumbnails */}
              <div className="grid grid-cols-4 gap-2 mt-auto relative z-10">
                {project.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setSelectedImage(idx); }}
                    className={`relative rounded-md overflow-hidden aspect-video border transition-all ${selectedImage === idx
                      ? `${theme.border} ring-2 ring-offset-1 ring-offset-slate-900 ${theme.text} opacity-100`
                      : 'border-white/5 opacity-50 hover:opacity-100'
                      }`}
                  >
                    <img src={img} className="w-full h-full object-cover" alt="" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* === RIGHT COLUMN: CONTENT === */}
          <div className="w-full md:w-[55%] bg-slate-900 flex flex-col overflow-hidden relative">

            {/* 
               HEADER WITH GLOW
               Added 'relative overflow-hidden' to container
               Added absolute divs for the Glow effect 
            */}
            <div className="relative p-6 md:p-8 border-b border-white/10 bg-slate-900/95 backdrop-blur z-10 sticky top-0 overflow-hidden">

              {/* --- HEADER GLOW START --- */}
              {/* A strong gradient blob behind the text */}
              <div className={`absolute -top-10 -left-10 w-64 h-64 ${theme.bg} rounded-full blur-3xl opacity-10 pointer-events-none`}></div>
              {/* A subtle gradient strip across the header */}
              <div className={`absolute top-0 left-0 w-full h-full bg-gradient-to-r ${theme.from} to-transparent opacity-10 blur-xl pointer-events-none`}></div>
              {/* --- HEADER GLOW END --- */}

              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${theme.border} ${theme.text} bg-white/5`}>
                    v1.0 Release
                  </span>
                  <div className="h-px flex-1 bg-gradient-to-r from-white/10 to-transparent" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-2 drop-shadow-lg">{project.title}</h2>
                <p className="text-slate-400 text-lg">Detailed Project Architecture</p>
              </div>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 md:overflow-y-auto custom-scrollbar p-6 md:p-8 space-y-8">

              {/* Description */}
              <section>
                <h3 className="flex items-center gap-2 text-white font-semibold text-lg mb-4">
                  <FaStar className={theme.text} /> Overview
                </h3>
                <p className="text-slate-300 leading-relaxed font-light text-lg">
                  {project.description}
                </p>
              </section>

              {/* Tech Stack */}
              <section>
                <h3 className="flex items-center gap-2 text-white font-semibold text-lg mb-4">
                  <FaCode className={theme.text} /> Technologies
                </h3>
                <div className="flex flex-wrap gap-2">
                  {project.tech.map((tech, i) => (
                    <span
                      key={i}
                      className="px-3 py-1.5 rounded-lg text-sm bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 transition-colors"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </section>

              {/* Features Grid */}
              <section>
                <h3 className="flex items-center gap-2 text-white font-semibold text-lg mb-4">
                  <FaLayerGroup className={theme.text} /> Key Features
                </h3>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {[
                    "Real-time Data Sync", "Responsive UI/UX",
                    "Secure Authentication", "Cloud Integration"
                  ].map((feat, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-400 text-sm p-3 rounded-lg bg-white/[0.02] border border-white/5">
                      <div className={`w-1.5 h-1.5 rounded-full ${theme.bg}`} />
                      {feat}
                    </li>
                  ))}
                </ul>
              </section>

              {/* Developer Note */}
              {project.devNote && (
                <section className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
                  <h3 className="flex items-center gap-2 text-amber-400 font-semibold text-sm mb-2">
                    ⚠️ Developer Note
                  </h3>
                  <p className="text-amber-200/80 text-sm leading-relaxed">
                    {project.devNote}
                  </p>
                </section>
              )}
            </div>

            {/* Footer Actions */}
            <div className="p-6 md:p-8 border-t border-white/10 bg-slate-900 z-10">
              <div className="flex gap-4">
                <a
                  href={project.live}
                  target="_blank"
                  rel="noreferrer"
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white shadow-lg transition-transform active:scale-95 bg-gradient-to-r ${theme.from} ${theme.to} hover:opacity-90`}
                >
                  <FaExternalLinkAlt /> Live Demo
                </a>
                <a
                  href={project.github}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                >
                  <FaGithub /> Source Code
                </a>
              </div>
            </div>

          </div>
        </motion.div>
      </motion.div>

      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(0,0,0,0.2); }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
      `}</style>
    </AnimatePresence>
  );
};

export default Information;